import { Typography, Card, CardMedia, Grid, Box, Button, LaunchOutlinedIcon } from "src/components";
import placeholderImage from "src/assets/placeholders/140x140.png";

const IMAGE_HEIGHT = 160;

export default function ProductCards({ products }) {
  if (!products?.length) return null;

  return (
    <Box sx={{ my: 3 }}>
      <Grid container columnSpacing={2} rowSpacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
        {products.map((product, index) => {
          const hasLink = Boolean(product.href);
          const hasButton = Boolean(product.buttonText);
          const isClickable = hasLink && hasButton;

          return (
            <Grid
              size={{ xs: 2, sm: 2, md: 3 }}
              key={index}
              sx={{ display: "flex" }}
            >
              <Card
                component={isClickable ? "a" : "div"}
                href={isClickable ? product.href : undefined}
                target={isClickable ? "_blank" : undefined}
                rel={isClickable ? "noopener noreferrer sponsored" : undefined}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  width: "100%",
                  backgroundColor: "#FFFFFF",
                  borderRadius: 2,
                  boxShadow: 2,
                  textDecoration: "none",
                  color: "inherit",
                  cursor: isClickable ? "pointer" : "default",
                  transition: isClickable ? "transform 0.2s ease, box-shadow 0.2s ease" : "none",
                  border: "1px solid #e0e0e0",
                  ...(isClickable && {
                    "&:hover": {
                      boxShadow: 5,
                      transform: { md: "translateY(-4px)", xs: "none" },
                      borderColor: "#bdbdbd",
                    },
                  }),
                }}
              >
                <CardMedia
                  component="img"
                  image={product.imageUrl || placeholderImage}
                  alt={product.title}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage;
                  }}
                  sx={{
                    height: IMAGE_HEIGHT,
                    width: "calc(100% - 16px)",
                    margin: "8px 8px 0 8px",
                    objectFit: "cover",
                    backgroundColor: "#eee",
                    borderRadius: 2,
                  }}
                />

                <Box
                  sx={{
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      lineHeight: 1.3,
                    }}
                  >
                    {product.title}
                  </Typography>

                  {product.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        mb: 1,
                      }}
                    >
                      {product.description}
                    </Typography>
                  )}

                  {hasButton && (
                    <Box sx={{ mt: "auto", pt: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        disableRipple
                        endIcon={<LaunchOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => e.preventDefault()}
                        sx={{
                          fontWeight: "bold",
                          textTransform: "none",
                          p: 0,
                          minWidth: 0,
                          color: "secondary.main",
                          pointerEvents: "none",
                          "&:hover": { 
                            textDecoration: "underline", 
                            backgroundColor: "transparent" 
                          },
                        }}
                      >
                        {product.buttonText}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}